// src/hooks/usePushPermission.js
//
// P4-2 — Push permission state + akıllı prompt timing.
//
// Returns:
//   state: "unsupported" | "default" | "denied" | "granted"
//   shouldPrompt: boolean — permission "default" + 7gun dismiss yok
//   askPermission()       — Notification.requestPermission + token register
//   dismiss()             — 7gun banner gizle
//   pending: boolean      — askPermission cagrisi sirasinda
//
// Timing felsefesi:
//   - Default state'te bile hemen sormaz; caller `shouldPrompt`'a bakıp banner
//     render eder
//   - Dismissal localStorage'da 7 gun saklanir
//   - Permission "denied" ise hicbir sey gosterilmez (kullanici reddetti)
//   - Permission "granted" ise zaten init oldu, banner cikmaz

import { useCallback, useEffect, useMemo, useState } from "react";
import { auth } from "../firebase";
import {
  isPushSupported,
  getPushPermissionState,
  initPushNotifications,
} from "../services/pushNotifications";

const DISMISS_KEY = "mlsa.pushPermission.dismissedAt";
const DISMISS_DURATION_MS = 7 * 86400 * 1000;

/**
 * "7 gün gizle" damgası TAZE mi? — SAF ve SENKRON.
 * `localStorage.getItem` senkrondur; bu cevabı bir effect'e ertelemek için
 * hiçbir sebep yok ve ertelemenin ölçülmüş bir bedeli var (aşağıya bak).
 */
function kapatildiMi(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    const at = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(at) && at > 0 && Date.now() - at < DISMISS_DURATION_MS;
  } catch (_) {
    return false;
  }
}

export default function usePushPermission() {
  const [state, setState] = useState(() =>
    typeof window !== "undefined" ? getPushPermissionState() : "unsupported",
  );
  // ── #992 SINIFI: SENKRON BİLİNEBİLEN CEVABI EFFECT'E ERTELEMEK ──────────
  // Eskiden `dismissed` `false` ile başlıyor, gerçek değeri bir `useEffect`
  // okuyordu. Effect'ler BOYAMADAN SONRA koşar → afişi 7 gün gizlemiş bir
  // kullanıcı için sıra şuydu:
  //     kare N    `dismissed=false` → afiş ÇİZİLİR (h=64)
  //     kare N+1  effect okur → `dismissed=true` → afiş SÖKÜLÜR
  // Altındaki her şey 64 px zıplıyordu — hem de kullanıcının "bir daha
  // gösterme" dediği afiş için. Cevap render sırasında BİLİNEBİLİR
  // (`localStorage` senkron), o yüzden artık TÜRETİLİYOR: bekleyecek bir
  // şey yok, sökülecek bir kare de yok.
  //
  // ⚠️ `oturumdaKapatildi` KALDI ve KASITLI: `localStorage` yazımı
  // reddedilebilir (özel sekme / kota). Eski kod o durumda bile afişi
  // kapatıyordu; yalnız depoya bakan bir sürüm × düğmesini ÖLDÜRÜRDÜ.
  const [oturumdaKapatildi, setOturumdaKapatildi] = useState(false);
  const dismissed = useMemo(
    () => oturumdaKapatildi || kapatildiMi(),
    [oturumdaKapatildi],
  );
  const [pending, setPending] = useState(false);

  // Permission state'i taraf değişikliklerinde tazele (ör. settings'ten döndü)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onVisibility = () => setState(getPushPermissionState());
    window.addEventListener("focus", onVisibility);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onVisibility);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const askPermission = useCallback(async () => {
    if (pending) return { ok: false, reason: "pending" };
    if (!auth.currentUser) return { ok: false, reason: "no_auth" };
    if (!isPushSupported()) return { ok: false, reason: "unsupported" };
    setPending(true);
    try {
      const r = await initPushNotifications({ requestPermission: true });
      setState(getPushPermissionState());
      return r;
    } finally {
      setPending(false);
    }
  }, [pending]);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_) {}
    setOturumdaKapatildi(true);
  }, []);

  const shouldPrompt =
    isPushSupported() &&
    state === "default" &&
    !dismissed &&
    !!auth.currentUser;

  return { state, shouldPrompt, pending, askPermission, dismiss };
}
